/* Verbatim port of parseInput/normalize from service/review/index.html
 * (~884-944). Bodies copied exactly; only type annotations added. (Batch C:
 * the loose `any`s became `unknown` + isRec() narrowing — parseInput is THE
 * untyped boundary of the whole app, arbitrary uploaded/pasted JSON, so the
 * branch structure below IS the runtime validation; the RawPayload casts at
 * the accepted exits mark exactly where the boundary claim is made.)
 */
import { fixMojibake, slugify } from '../lib/format.ts';
import type { NormalizedAsset, NormalizedPayload, RawEdge, RawPayload } from '../types/contract.ts';
import type { AppPayloadState } from './state-shape.ts';
import { isSkippedLayerOpportunity, type ScopeProfile } from './scope.ts';
import type { PlaneStatusMap } from './planes.ts';

export interface ParsedInput {
  brand: string;
  payload: RawPayload;
  dossier: string | null;
  /* Profile-scope provenance (design F1) — read from the TOP-LEVEL run JSON
   * (result.skippedLayers / result.profile), a sibling of `payload`, not from
   * the contract itself. `[]` / null for a bare payload or a markdown paste. */
  skippedLayers: string[];
  profileScope: ScopeProfile | null;
  /* Per-plane run status (H1) — read from result.enrichment.planeStatus, a
   * sibling of `payload`. null for a legacy run (pre-planeStatus), a bare
   * payload, or a markdown paste; callers then render legacy neutral copy. */
  planeStatus: PlaneStatusMap | null;
  /* The run's own generation stamp (result.generatedAt, ResultFile) — the
   * LOADED payload's identity date. Cockpit zone-5 provenance reads THIS,
   * never a rollup row, so provenance and data cannot disagree by
   * construction (design review 2026-07-12, cockpit blocker fix 2). null on
   * a bare payload / markdown paste / legacy unstamped run. */
  generatedAt: string | null;
}

/** unknown-narrowing helper for the parse boundary: a plain object we can
 * index (arrays included — the original's truthy-object checks matched
 * arrays too, so this must as well). */
function isRec(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object';
}

/* ── parsing (accepts /research responses, bare payloads, or md) ── */
export function parseInput(text: string, filename?: string): ParsedInput {
  let parsedJson: unknown = null;
  try { parsedJson = JSON.parse(text); } catch {}
  const data = isRec(parsedJson) ? parsedJson : null;
  let md: string | null = null, brand: string | null = null, payload: RawPayload | null = null;
  const dataPayload = data && isRec(data.payload) ? data.payload : null;
  if (data && dataPayload && Array.isArray(dataPayload.assets)){
    payload = dataPayload as unknown as RawPayload; brand = (data.brand ?? null) as string | null;
    if (typeof data.result === 'string') md = fixMojibake(data.result);
  }
  else if (data && typeof data.result === 'string'){ md = data.result; brand = (data.brand ?? null) as string | null; }
  else if (data && Array.isArray(data.assets)){ payload = data as unknown as RawPayload; }
  else { md = text; }
  if (md && !payload){
    md = fixMojibake(md);
    const m = md.match(/```json\s*([\s\S]*?)```/);
    if (!m) throw new Error('No ```json block found in the result text.');
    payload = JSON.parse(m[1] ?? '') as RawPayload;
  }
  if (!payload || !Array.isArray(payload.assets)) throw new Error('Payload has no assets array.');
  // Scope provenance lives on the run-JSON wrapper (`data`), never on the bare
  // contract — absent (unscoped) for a bare-payload upload or a markdown paste.
  const skippedLayers: string[] = data && Array.isArray(data.skippedLayers)
    ? data.skippedLayers.filter((x): x is string => typeof x === 'string' && !!x) : [];
  const profileScope: ScopeProfile | null =
    data && data.profile && typeof data.profile === 'object' ? data.profile as ScopeProfile : null;
  // Per-plane run status lives on result.enrichment.planeStatus (H1) — a
  // sibling of `payload` on the run-JSON wrapper, absent on a bare payload or
  // a legacy run that predates the stamp.
  const ps = data && isRec(data.enrichment) ? data.enrichment.planeStatus : null;
  const planeStatus: PlaneStatusMap | null =
    ps && typeof ps === 'object' ? ps as PlaneStatusMap : null;
  // The run's own generation stamp — a sibling of `payload` on the run-JSON
  // wrapper (ResultFile.generatedAt); absent on bare payloads / md pastes.
  const generatedAt: string | null =
    data && typeof data.generatedAt === 'string' && data.generatedAt ? data.generatedAt : null;
  return { brand: brand || payload.brand || (filename||'').replace(/\.[^.]+$/,'') || 'Unknown brand',
           payload, dossier: md, skippedLayers, profileScope, planeStatus, generatedAt };
}

export function normalize(payload: RawPayload): NormalizedPayload {
  const assets: NormalizedAsset[] = payload.assets.map((a) => {
    const id = a.id || slugify(a.title || a.url || 'asset');
    return Object.assign({
      assetType:'website' as const, platform:null, role:(a.isCoreEntity ? 'core' : 'profile') as NormalizedAsset['role'],
      scope:'global' as const, region:null, status:'unknown' as const,
      controlLevel: (a.category === 'owned' ? 'full' : 'none') as NormalizedAsset['controlLevel'],
      sentiment:null, verified:false, fields:null
    }, a, { id, title: fixMojibake(a.title) });
  });
  const seen: Record<string, number> = {};
  assets.forEach((a) => { const n = seen[a.id]; if (n){ seen[a.id] = n + 1; a.id = a.id + '-' + (n + 1); } else seen[a.id] = 1; });
  const cores = assets.filter((a) => a.isCoreEntity);
  if (cores.length > 1){
    const keep = cores.find((a) => a.role === 'core') || cores[0];
    cores.forEach((a) => { if (a !== keep) a.isCoreEntity = false; });
  }
  let edges: RawEdge[] = Array.isArray(payload.edges) ? payload.edges.slice() : [];
  const ids = new Set(assets.map((a) => a.id));
  edges = edges.filter((e) => ids.has(e.from) && ids.has(e.to));
  const core = assets.find((a) => a.isCoreEntity) || assets[0];
  // FIX-audit F11: edges synthesized here to root/connect the tree are LAYOUT
  // artifacts, not observed links — stamp `synthetic: true` so measured surfaces
  // (the Graph "link map", the drawer's "linked (measured inbound)") can exclude
  // them or render "link graph not measured", never present them as observed.
  if (!edges.length && core){
    edges = assets.filter((a) => a.id !== core.id).map((a) => ({ from:a.id, to:core.id, relation:'associated-with' as const, synthetic:true }));
  } else if (core){
    const connected = new Set([core.id]); let grew = true;
    while (grew){ grew = false; edges.forEach((e) => {
      if (connected.has(e.from) && !connected.has(e.to)){ connected.add(e.to); grew = true; }
      if (connected.has(e.to) && !connected.has(e.from)){ connected.add(e.from); grew = true; } }); }
    assets.forEach((a) => { if (!connected.has(a.id)) edges.push({ from:a.id, to:core.id, relation:'associated-with', synthetic:true }); });
  }
  return { assets, edges, conflicts: payload.conflicts || [], coverage: payload.coverageSummary || {},
           serp: payload.serp || null, serps: payload.serps || [],
           opportunities: payload.opportunities || [],
           entity: payload.entity || null, knowledgePanel: payload.knowledgePanel || null,
           wikidata: payload.wikidata || null,
           aiNarrative: payload.aiNarrative || null, impact: payload.impact || null,
           linkGraph: payload.linkGraph || null, peers: payload.peers || null,
           synthesis: payload.synthesis || null,
           brandLogo: payload.brandLogo || null,
           entityMap: Array.isArray(payload.entities) && payload.entities.length ? payload.entities : null };
}

/* NOT a port — new code synthesized for this task. Mirrors the pure subset
 * of the old load()'s STATE merge (service/review/index.html ~3415-3420:
 * `STATE = Object.assign(STATE, { brand, dossier, selected: null }, norm)`)
 * with every DOM/localStorage/render side effect stripped out (those are
 * F2 material). Exists so tests — and eventually F2's load() — can build
 * the same AppPayloadState the derivations expect without duplicating this
 * merge logic per call site. */
export function buildState(
  parsed: { brand: string; dossier: string | null; skippedLayers?: string[]; profileScope?: ScopeProfile | null; planeStatus?: PlaneStatusMap | null; generatedAt?: string | null },
  normalized: NormalizedPayload,
): AppPayloadState {
  const skippedLayers = parsed.skippedLayers || [];
  const profileScope = parsed.profileScope || null;
  const planeStatus = parsed.planeStatus || null;
  // F1 coverage guard: drop opportunities that belong to a deliberately-
  // skipped layer so no §0/§6/§7 derivation recommends "claim X" for a layer
  // the run never checked. The suppression is NOT silent — the §0 scope-
  // provenance line (ws/strip.ts) names every skipped layer, so the skip is
  // surfaced honestly while the misleading recommendation is withheld.
  const opportunities = skippedLayers.length
    ? (normalized.opportunities || []).filter((o) => !isSkippedLayerOpportunity(o, skippedLayers))
    : normalized.opportunities;
  return {
    brand: parsed.brand,
    dossier: parsed.dossier,
    skippedLayers,
    profileScope,
    planeStatus,
    generatedAt: parsed.generatedAt || null,
    runFile: null, // set by loadPayload() from the archive filename (state-shape.ts)
    selected: null,
    nodes: new Map(),
    ...normalized,
    opportunities,
  } as AppPayloadState;
}
